/**
 * ActionHistory Store with ReactFlow Support for complete undo/redo
 * It captures the following states:
 * The canvasStore
 * The trackStore
 * The componentConfigStore
 * The ReactFlow instance state
 */

/* eslint-disable no-param-reassign -- immer draft mutations in Zustand */

import { useCallback } from 'react';
import {
  Node as ReactFlowNode,
  Edge as ReactFlowEdge,
  Viewport
} from 'reactflow';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { getReactFlowInstance } from './reactFlowInstance';
import { sendToBus } from '../../../../../components/Bus';

import i18n from '~/common/helpers/i18n';
import {
  useCanvasStore,
  CanvasNode,
  CanvasEdge,
  LevelPathItem
} from '~/scenes/Build/scenes/FlowBuilder/stores/canvasStore';
import {
  useComponentConfigStore,
  ComponentConfig
} from '~/scenes/Build/scenes/FlowBuilder/stores/componentConfigStore';
import {
  useTrackStore,
  Track
} from '~/scenes/Build/scenes/FlowBuilder/stores/trackStore';

export interface ReactFlowVisualState {
  viewport: Viewport;
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

export interface ActionHistorySnapshot {
  id: string;
  timestamp: number;
  operation: string;

  // Canvas Stores (source of truth)
  canvasState: {
    nodes: Map<string, CanvasNode>;
    edges: Map<string, CanvasEdge>;
    currentLevelName: string;
    currentLevelPath: LevelPathItem[];
  };
  trackState: {
    tracks: Map<string, Track>;
  };
  componentConfigState: {
    configurations: Map<string, ComponentConfig>;
  };

  // ReactFlow state (for visual restoration)
  reactFlowState?: ReactFlowVisualState;
}

export interface ActionHistoryState {
  // ActionHistory stacks
  undoStack: ActionHistorySnapshot[];
  redoStack: ActionHistorySnapshot[];

  // Configuration
  maxHistorySize: number;
  isRecording: boolean;
  captureReactFlowState: boolean;
  undoRedoDisabledBy: string[];

  // Actions
  captureSnapshot: (
    operation: string,
    reactFlowState?: ReactFlowVisualState
  ) => void;
  undo: (restoreReactFlow?: boolean) => boolean;
  redo: (restoreReactFlow?: boolean) => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;

  // Recording control
  startRecording: () => void;
  stopRecording: () => void;
  enableReactFlowCapture: () => void;
  disableReactFlowCapture: () => void;

  // Undo/Redo control
  enableUndoRedo: (disabler: string) => void;
  disableUndoRedo: (disabler: string) => void;
  isUndoRedoDisabled: () => boolean;

  // Batch operations
  startBatch: () => void;
  endBatch: (operation: string, reactFlowState?: ReactFlowVisualState) => void;

  // Debug
  getUndoStackSize: () => number;
  getRedoStackSize: () => number;
  getLastOperation: () => string | undefined;
  getLastSnapshot: () => ActionHistorySnapshot | undefined;
}

// Batch state
let batchInProgress = false;
let batchStartSnapshot: ActionHistorySnapshot | null = null;

// Debounce state for undo/redo operations
let lastUndoRedoTime = 0;
const UNDO_REDO_DEBOUNCE_MS = 500;

function captureReactFlowVisualState(): ReactFlowVisualState | undefined {
  const reactFlowInstance = getReactFlowInstance();

  if (!reactFlowInstance) {
    return undefined;
  }

  try {
    return {
      viewport: reactFlowInstance.getViewport(),
      nodes: reactFlowInstance.getNodes(),
      edges: reactFlowInstance.getEdges()
    };
  } catch (error) {
    console.error(
      '[ActionHistoryStore] Failed to capture ReactFlow state:',
      error
    );
    return undefined;
  }
}

function restoreReactFlowVisualState(state: ReactFlowVisualState): void {
  const reactFlowInstance = getReactFlowInstance();

  if (!reactFlowInstance) {
    return;
  }

  try {
    reactFlowInstance.setViewport(state.viewport, { duration: 200 });
    reactFlowInstance.setNodes(state.nodes);
    reactFlowInstance.setEdges(state.edges);
  } catch (error) {
    console.error(
      '[ActionHistoryStore] Failed to restore ReactFlow state:',
      error
    );
  }
}

function createSnapshotId(): string {
  return `snapshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function cloneMap<K, V>(map: Map<K, V>): Map<K, V> {
  const cloned = new Map<K, V>();
  map.forEach((value, key) => {
    const clonedValue =
      typeof structuredClone !== 'undefined'
        ? structuredClone(value)
        : JSON.parse(JSON.stringify(value));
    cloned.set(key, clonedValue);
  });
  return cloned;
}

function captureCurrentState(
  operation: string,
  includeReactFlow: boolean
): ActionHistorySnapshot {
  const canvasStore = useCanvasStore.getState();
  const trackStore = useTrackStore.getState();
  const componentConfigStore = useComponentConfigStore.getState();

  const snapshot: ActionHistorySnapshot = {
    id: createSnapshotId(),
    timestamp: Date.now(),
    operation,
    canvasState: {
      nodes: cloneMap(canvasStore.nodes),
      edges: cloneMap(canvasStore.edges),
      currentLevelName: canvasStore.currentLevelName,
      currentLevelPath: canvasStore.currentLevelPath
    },
    trackState: {
      tracks: cloneMap(trackStore.tracks)
    },
    componentConfigState: {
      configurations: cloneMap(componentConfigStore.configurations)
    }
  };

  if (includeReactFlow) {
    snapshot.reactFlowState = captureReactFlowVisualState();
  }

  return snapshot;
}

function showSnackBar(text: string) {
  sendToBus({
    event: {
      type: 'SHOW',
      text,
      duration: 4000
    },
    to: 'snackbar'
  });
}

export const useActionHistoryStore = create<ActionHistoryState>()(
  devtools(
    immer((set, get) => ({
      undoStack: [],
      redoStack: [],
      maxHistorySize: 25,
      isRecording: true,
      captureReactFlowState: true,
      undoRedoDisabledBy: [],

      captureSnapshot: (operation, reactFlowState) => {
        if (!get().isRecording || batchInProgress) {
          return;
        }

        const includeReactFlow = get().captureReactFlowState;
        const snapshot = reactFlowState
          ? {
              ...captureCurrentState(operation, false),
              reactFlowState
            }
          : captureCurrentState(operation, includeReactFlow);

        set(state => {
          state.undoStack.push(snapshot);
          state.redoStack = [];

          if (state.undoStack.length > state.maxHistorySize) {
            state.undoStack.shift();
          }
        });
      },

      undo: (restoreReactFlow = true) => {
        if (get().undoRedoDisabledBy.length > 0) {
          return false;
        }

        const now = Date.now();
        if (now - lastUndoRedoTime < UNDO_REDO_DEBOUNCE_MS) {
          return false;
        }

        const currentState = get();

        if (currentState.undoStack.length === 0) {
          return false;
        }

        lastUndoRedoTime = now;

        const snapshotToRestore =
          currentState.undoStack[currentState.undoStack.length - 1];

        const currentSnapshot = captureCurrentState(
          snapshotToRestore.operation,
          currentState.captureReactFlowState
        );

        set(draft => {
          draft.undoStack.pop();
          draft.redoStack.push(currentSnapshot);
        });

        // eslint-disable-next-line @typescript-eslint/no-use-before-define -- defined after store export; safe at runtime
        restoreSnapshot(snapshotToRestore, restoreReactFlow);
        showSnackBar(
          `${i18n.t('scenes.design.messages.undoRedo.undo')}: ${snapshotToRestore.operation}`
        );
        return true;
      },

      redo: (restoreReactFlow = true) => {
        if (get().undoRedoDisabledBy.length > 0) {
          return false;
        }

        const now = Date.now();
        if (now - lastUndoRedoTime < UNDO_REDO_DEBOUNCE_MS) {
          return false;
        }

        const currentState = get();

        if (currentState.redoStack.length === 0) {
          return false;
        }

        lastUndoRedoTime = now;

        const snapshotToRestore =
          currentState.redoStack[currentState.redoStack.length - 1];

        const currentSnapshot = captureCurrentState(
          snapshotToRestore.operation,
          currentState.captureReactFlowState
        );

        set(draft => {
          draft.redoStack.pop();
          draft.undoStack.push(currentSnapshot);
        });

        // eslint-disable-next-line @typescript-eslint/no-use-before-define -- defined after store export; safe at runtime
        restoreSnapshot(snapshotToRestore, restoreReactFlow);
        showSnackBar(
          `${i18n.t('scenes.design.messages.undoRedo.redo')}: ${snapshotToRestore.operation}`
        );
        return true;
      },

      canUndo: () =>
        get().undoStack.length > 0 && get().undoRedoDisabledBy.length === 0,
      canRedo: () =>
        get().redoStack.length > 0 && get().undoRedoDisabledBy.length === 0,

      clearHistory: () => {
        set(state => {
          state.undoStack = [];
          state.redoStack = [];
        });
      },

      startRecording: () => {
        set(state => {
          state.isRecording = true;
        });
      },

      stopRecording: () => {
        set(state => {
          state.isRecording = false;
        });
      },

      enableReactFlowCapture: () => {
        set(state => {
          state.captureReactFlowState = true;
        });
      },

      disableReactFlowCapture: () => {
        set(state => {
          state.captureReactFlowState = false;
        });
      },

      enableUndoRedo: (disabler: string) => {
        set(state => {
          state.undoRedoDisabledBy = state.undoRedoDisabledBy.filter(
            name => name !== disabler
          );
        });
      },

      disableUndoRedo: (disabler: string) => {
        set(state => {
          if (!state.undoRedoDisabledBy.includes(disabler)) {
            state.undoRedoDisabledBy.push(disabler);
          }
        });
      },

      isUndoRedoDisabled: () => get().undoRedoDisabledBy.length > 0,

      startBatch: () => {
        if (batchInProgress) {
          return;
        }

        batchInProgress = true;
        batchStartSnapshot = captureCurrentState(
          'Batch Start',
          get().captureReactFlowState
        );
      },

      endBatch: (operation, reactFlowState) => {
        if (!batchInProgress) {
          return;
        }

        batchInProgress = false;

        if (batchStartSnapshot && get().isRecording) {
          const includeReactFlow = get().captureReactFlowState;
          const snapshot = reactFlowState
            ? {
                ...captureCurrentState(operation, false),
                reactFlowState
              }
            : captureCurrentState(operation, includeReactFlow);

          set(state => {
            state.undoStack.push(snapshot);
            state.redoStack = [];

            if (state.undoStack.length > state.maxHistorySize) {
              state.undoStack.shift();
            }
          });
        }

        batchStartSnapshot = null;
      },

      getUndoStackSize: () => get().undoStack.length,
      getRedoStackSize: () => get().redoStack.length,
      getLastOperation: () => {
        const stack = get().undoStack;
        return stack.length > 0 ? stack[stack.length - 1].operation : undefined;
      },
      getLastSnapshot: () => {
        const stack = get().undoStack;
        return stack.length > 0 ? stack[stack.length - 1] : undefined;
      }
    })),
    { name: 'HistoryStore' }
  )
);

function restoreSnapshot(
  snapshot: ActionHistorySnapshot,
  restoreReactFlow: boolean
): void {
  const canvasStore = useCanvasStore.getState();
  const trackStore = useTrackStore.getState();
  const componentConfigStore = useComponentConfigStore.getState();
  const actionHistoryStore = useActionHistoryStore.getState();

  actionHistoryStore.stopRecording();

  try {
    canvasStore.clearCanvas();
    snapshot.canvasState.nodes.forEach(node => {
      canvasStore.addNode(node);
    });
    snapshot.canvasState.edges.forEach(edge => {
      canvasStore.addEdge(edge);
    });
    canvasStore.setCurrentLevel(
      snapshot.canvasState.currentLevelPath,
      snapshot.canvasState.currentLevelName
    );

    trackStore.clearTracks();
    snapshot.trackState.tracks.forEach(track => {
      trackStore.addTrack(track);
    });

    componentConfigStore.clearConfigs();
    snapshot.componentConfigState.configurations.forEach((config, id) => {
      componentConfigStore.setConfig(id, config);
    });

    if (restoreReactFlow && snapshot.reactFlowState) {
      setTimeout(() => {
        restoreReactFlowVisualState(snapshot.reactFlowState!);
      }, 50);
    }
  } finally {
    actionHistoryStore.startRecording();
  }
}

export function useActionHistoryKeyboardShortcuts(restoreReactFlow = true) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      if (modifierKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();

        if (event.shiftKey) {
          if (useActionHistoryStore.getState().canRedo()) {
            useActionHistoryStore.getState().redo(restoreReactFlow);
          }
        } else if (useActionHistoryStore.getState().canUndo()) {
          useActionHistoryStore.getState().undo(restoreReactFlow);
        }
      }
    },
    [restoreReactFlow]
  );

  return handleKeyDown;
}
